import { CSSProperties } from 'react';

const monospace: CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
  lineHeight: 1.6,
};

const summaryStyle: CSSProperties = {
  cursor: 'pointer',
  userSelect: 'none',
  color: '#555',
};

const keyStyle: CSSProperties = {
  color: '#0d6efd',
  marginRight: 4,
};

const stringStyle: CSSProperties = { color: '#198754' };
const numberStyle: CSSProperties = { color: '#7c3aed' };
const nullStyle: CSSProperties = { color: '#6c757d', fontStyle: 'italic' };

const renderLeaf = (value: unknown): JSX.Element => {
  if (value === null) return <span style={nullStyle}>null</span>;
  if (typeof value === 'string') {
    return <span style={stringStyle}>&quot;{value}&quot;</span>;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span style={numberStyle}>{String(value)}</span>;
  }
  return <span>{String(value)}</span>;
};

const isLeaf = (value: unknown): boolean =>
  value === null || (typeof value !== 'object' && !Array.isArray(value));

const renderNode = (value: unknown, depth: number): JSX.Element => {
  if (isLeaf(value)) return renderLeaf(value);
  const isArray = Array.isArray(value);
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [i, v] as const)
    : Object.entries(value as Record<string, unknown>);
  const summary = isArray
    ? `Array(${entries.length})`
    : `Object {${entries.length}}`;
  return (
    <details open={depth < 1}>
      <summary style={summaryStyle}>{summary}</summary>
      <div style={{ marginLeft: 16 }}>
        {entries.map(([k, v]) => (
          <div key={String(k)}>
            <span style={keyStyle}>{String(k)}:</span>
            {renderNode(v, depth + 1)}
          </div>
        ))}
      </div>
    </details>
  );
};

interface JsonTreeProps {
  value: unknown;
}

export const JsonTree = ({ value }: JsonTreeProps): JSX.Element => (
  <div style={monospace}>{renderNode(value, 0)}</div>
);
